import {defineStore} from 'pinia';
import {computed, reactive, ref} from 'vue';
import * as KlipApi from '@/api/klip-api.proxy';
import {klipApiProxy, useKlipApiProxy} from '@/plugins/proxy-client';
import {handleAbortError, KlAbortController} from '@/app/shared/helpers/kl-abort-controller';
import {IpproDatatable} from "@/app/shared/components/ippro-datatable/ippro-datatable-declare";
import {first, isEmpty, isEqual} from 'lodash-es';
import {useRoute, useRouter} from '@/router/router-vue3';
import QueryFilter from '@/app/shared/helpers/queryFilter';
import DateUtil from '@/app/shared/helpers/date-util';
import {
    IMapRequestsToConfirmData, KlipApiProxy,
    MapRequestListViewUna,
    MapRequestsRepliedData, MapRequestsRepliedOrderField,
    MapRequestsToConfirmData,
    MapRequestsToReplyData, MapRequestsToReplyOrderField, OrderDirection, SearchField, SearchOption
} from '@/api/klip-api.proxy';
import userLocalStorage from '@/storage/user-local-storage';
import * as UnaModel from './shared/una-model';
import {
    create_MapRequestContext
} from '@/app/una/maprequest-store-context';
import {IMapRequestListItem} from './shared/una-model';
import {useErrorStore} from '@/stores/error-store';
import * as repl from 'node:repl';
import {saveAs} from 'file-saver';

export const useUnaStore = defineStore('una-store', () => {
    const proxy = useKlipApiProxy();
    const router = useRouter();
    const route = useRoute();
    const queryFilter = new QueryFilter(router);

    //
    // const _una_GetMapRequestsToConfirm_AbortController = new KlAbortController();
    // const _una_GetMapRequestsToConfirmCount_AbortController = new KlAbortController();
    // const _una_GetMapRequestsToReply_AbortController = new KlAbortController();
    // const _una_GetMapRequestsToReplyCount_AbortController = new KlAbortController();

    const queryTypeDefinition = {
        orderBy: 'string',
        orderDirection: KlipApi.OrderDirection,
        searchField: KlipApi.SearchField,
        mapRequestStatus: 'string',
        isArchived: 'boolean',
        startDate: 'Date',
        endDate: 'Date',
        checkWorkingPeriod: 'boolean',
    };

    const searchOptionToConfirmLocalStorageKey = 'una_search_to_confirm_pa_organisation_id';
    const searchOptionToReplyLocalStorageKey = 'una_search_to_reply_pa_organisation_id';
    const searchOptionRepliedLocalStorageKey = 'una_search_replied_pa_organisation_id';

    const toConfirm = ref(create_MapRequestContext<KlipApi.IGetMapRequestsToConfirmInput>(router, route, queryTypeDefinition, searchOptionToConfirmLocalStorageKey, _fetchMapRequestsToConfirm, _getCleanToConfirmInput));
    const toReply = ref(create_MapRequestContext<KlipApi.IGetMapRequestsToReplyInput>(router, route, queryTypeDefinition, searchOptionToReplyLocalStorageKey, _fetchMapRequestsToReply, _getCleanToReplyInput));
    const replied = ref(create_MapRequestContext<KlipApi.IGetMapRequestsRepliedInput>(router, route, queryTypeDefinition, searchOptionRepliedLocalStorageKey, _fetchMapRequestsReplied, _getCleanRepliedInput));

    const noZonesOrMapRequests = ref<boolean>(false);
    const viewModelLoading = ref<boolean>(true);
    const toConfirmData = ref<UnaModel.IMapRequestsToConfirm>(null);
    const toReplyData = ref<UnaModel.IMapRequestsToReply>(null);
    const repliedData = ref<UnaModel.IMapRequestsReplied>(null);


    async function initUnaView() {
        const response = await proxy.una_GetMapRequestsListView();
        await _getMapRequestCounts(response.result);
    }

    async function initToConfirm(cellParser?: (row: IpproDatatable.IRow, column: IpproDatatable.IColumn) => any) {
        toConfirm.value.init(toConfirmData.value, cellParser);
    }

    async function initToReply(cellParser?: (row: IpproDatatable.IRow, column: IpproDatatable.IColumn) => any) {
        toReply.value.init(toReplyData.value, cellParser);
    }

    async function initReplied(cellParser?: (row: IpproDatatable.IRow, column: IpproDatatable.IColumn) => any) {
        replied.value.init(repliedData.value, cellParser);
    }

    function reset() {
        toConfirm.value.reset();
        toReply.value.reset();
        replied.value.reset();
    }

    async function confirmMapRequests(mapRequests: KlipApi.ConfirmMapRequestsInput[]): Promise<void> {
        useErrorStore().setBypassError(['404']);
        await klipApiProxy.una_ConfirmMapRequests(mapRequests);
        _confirmMapRequestsMutation(mapRequests);
    }

    async function notInvolved(input: KlipApi.MapRequestNotInvolvedInput): Promise<void> {
        useErrorStore().setBypassError(['405']);
        await klipApiProxy.una_NotInvolved(input);
        _notInvolvedMutation(input);
    }

    async function downloadCurrentReplied(): Promise<KlipApi.FileResponse> {
        const input = KlipApi.GetMapRequestsRepliedInput.fromJS(replied.value.mapRequestsInput);
        const response: KlipApi.FileResponse = await useKlipApiProxy().document_DownloadMapRequestsReplied(input);

        return response;
    }

    function _confirmMapRequestsMutation(confirmedMapRequests: KlipApi.ConfirmMapRequestsInput[]) {
        if (!toConfirm.value) {
            return;
        }

        const idPairs = confirmedMapRequests.map((c: KlipApi.ConfirmMapRequestsInput): { mapRequestId: string, zoneId: string} => ({
            mapRequestId: c.mapRequestId,
            zoneId: c.zoneId,
        }));
        const confirmedRequests = toConfirm.value.__removeMapRequests(idPairs);

        toReply.value.__addMapRequests(confirmedRequests);
    }

    function _notInvolvedMutation(input: KlipApi.MapRequestNotInvolvedInput) {
        const notInvolvedMapRequests = toReply.value.__removeMapRequests([{
            mapRequestId: input.mapRequestId,
            zoneId: input.zoneId
        }]);

        replied.value.__addMapRequests(notInvolvedMapRequests);
    }

    function _getCleanToConfirmInput(input: KlipApi.IGetMapRequestsToConfirmInput): KlipApi.IGetMapRequestsToConfirmInput {

        function __cleanString(v: string): string | null {
            return (!v ? null : v);
        }

        return {
            query:  __cleanString(input.query),
            searchField: input.searchField,
            startDate: DateUtil.formatForApi(input.startDate, 'ISO'),
            endDate: DateUtil.formatForApi(input.endDate, 'ISO'),
            orderField: input.orderField,
            orderDirection: input.orderDirection || KlipApi.OrderDirection.None,
            offset: input.offset || 0,
            limit: input.limit || 0,
            organisationId: input.organisationId,
            utilityNetworkAuthorityZoneId: input.utilityNetworkAuthorityZoneId,
            searchOption: input.searchOption,
        }
    }

    function _getCleanToReplyInput(input: KlipApi.IGetMapRequestsToReplyInput): KlipApi.IGetMapRequestsToReplyInput {

        function __cleanString(v: string): string | null {
            return (!v ? null : v);
        }

        return {
            query:  __cleanString(input.query),
            searchField: input.searchField,
            startDate: DateUtil.formatForApi(input.startDate, 'ISO'),
            endDate: DateUtil.formatForApi(input.endDate, 'ISO'),
            orderField: input.orderField,
            orderDirection: input.orderDirection || KlipApi.OrderDirection.None,
            offset: input.offset || 0,
            limit: input.limit || 0,
            organisationId: input.organisationId,
            utilityNetworkAuthorityZoneId: input.utilityNetworkAuthorityZoneId,
            searchOption: input.searchOption,
        }
    }

    function _getCleanRepliedInput(input: KlipApi.IGetMapRequestsRepliedInput): KlipApi.IGetMapRequestsRepliedInput {

        function __cleanString(v: string): string | null {
            return (!v ? null : v);
        }

        return {
            query:  __cleanString(input.query),
            searchField: input.searchField,
            startDate: DateUtil.formatForApi(input.startDate, 'ISO'),
            endDate: DateUtil.formatForApi(input.endDate, 'ISO'),
            orderField: input.orderField,
            orderDirection: input.orderDirection || KlipApi.OrderDirection.None,
            offset: input.offset || 0,
            limit: input.limit || 0,
            organisationId: input.organisationId,
            utilityNetworkAuthorityZoneId: input.utilityNetworkAuthorityZoneId,
            searchOption: input.searchOption,

            isArchived: !!input.isArchived,
            zoneGeometry: __cleanString(input.zoneGeometry),
            zoneWkt: __cleanString(input.zoneWkt),

            isMapRequestId: !!input.isMapRequestId, // wordt dit gebruikt?
            isMapRequestInitiator: !!input.isMapRequestInitiator, // wordt dit gebruikt?
            isReference: !!input.isReference, // wordt dit gebruikt?
        }
    }

    async function _fetchMapRequestsToConfirm(apiInput: KlipApi.IGetMapRequestsToConfirmInput): Promise<{
        count: number,
        items: UnaModel.IMapRequestListItem[]
    }> {
        try {
            const input = KlipApi.GetMapRequestsToConfirmInput.fromJS(apiInput);
            const [countResponse, listResponse] = await Promise.all([
                proxy.una_GetMapRequestsToConfirmCount(input),
                proxy.una_GetMapRequestsToConfirm(input),
            ]);
            const count = countResponse.result;
            const items = listResponse.result.mapRequests.map((apiListItem: KlipApi.MapRequestToConfirmListItem): IMapRequestListItem => {
                return {
                    confirmationDate: apiListItem.confirmationDate, // confirmationDate is NOT a date, but a free text string (ex: "06/03/2026 - Binnen één werkdag")// DateUtil.formatDate(apiListItem.confirmationDate),
                    dateReceived: DateUtil.formatDate(apiListItem.dateReceived),
                    startDate: DateUtil.formatDate(apiListItem.startDate),
                    mapRequestType: apiListItem.mapRequestType,
                    organisation: apiListItem.organisation,
                    isOverdue: apiListItem.isOverdue,
                    mapRequestId: apiListItem.mapRequestId,
                    reference: apiListItem.reference,
                    unaZoneId: apiListItem.unaZoneId,
                    zoneName: apiListItem.zoneName,

                    checked: true, // auto check all after each fetch
                }
            });

            return { count, items };

        } catch (err) {
            handleAbortError(err);
            throw err;
        }
    }

    async function _fetchMapRequestsToReply(apiInput: KlipApi.IGetMapRequestsToReplyInput): Promise<{
        count: number,
        items: UnaModel.IMapRequestListItem[]
    }> {
        try {
            const input = KlipApi.GetMapRequestsToReplyInput.fromJS(apiInput);
            const [countResponse, listResponse] = await Promise.all([
                proxy.una_GetMapRequestsToReplyCount(input),
                proxy.una_GetMapRequestsToReply(input),
            ]);
            const count = countResponse.result;
            const items = listResponse.result.mapRequests.map((apiListItem: KlipApi.MapRequestToReplyListItem): IMapRequestListItem => {
                return {
                    dateReceived: DateUtil.formatDate(apiListItem.dateReceived),
                    startDate: DateUtil.formatDate(apiListItem.startDate),
                    mapRequestType: apiListItem.mapRequestType,
                    organisation: apiListItem.organisation,
                    mapRequestId: apiListItem.mapRequestId,
                    reference: apiListItem.reference,
                    unaZoneId: apiListItem.unaZoneId,
                    zoneName: apiListItem.zoneName,

                    isOverdue: false,
                }
            });

            return { count, items };

        } catch (err) {
            handleAbortError(err);
            throw err;
        }
    }

    async function _fetchMapRequestsReplied(apiInput: KlipApi.IGetMapRequestsRepliedInput): Promise<{
        count: number,
        items: UnaModel.IMapRequestListItem[]
    }> {
        try {
            const input = KlipApi.GetMapRequestsRepliedInput.fromJS(apiInput);
            const [countResponse, listResponse] = await Promise.all([
                proxy.una_GetMapRequestsRepliedCount(input),
                proxy.una_GetMapRequestsReplied(input),
            ]);
            const count = countResponse.result;
            const items = listResponse.result.mapRequests.map((apiListItem: KlipApi.MapRequestRepliedListItem): IMapRequestListItem => {
                return {
                    dateReceived: DateUtil.formatDate(apiListItem.dateReceived),
                    organisation: apiListItem.organisation,
                    requestor: apiListItem.requestor,

                    mapRequestId: apiListItem.mapRequestId,
                    reference: apiListItem.reference,
                    unaZoneId: apiListItem.unaZoneId,
                    zoneName: apiListItem.zoneName,

                    isArchived: apiListItem.isArchived,
                }
            });

            return { count, items };

        } catch (err) {
            handleAbortError(err);
            throw err;
        }
    }

    function _mapFromData<DATA_TYPE extends MapRequestsToConfirmData | MapRequestsToReplyData | MapRequestsRepliedData, RESPONSE_TYPE>(
        searchOption: string,
        count: number,
        data: DATA_TYPE
    ): RESPONSE_TYPE {

        const result = {
            count: count,
            mapRequestColumns: data.mapRequestColumns,
            getMapRequestsInput: data.getMapRequestsInput,
            defaultMapRequestsInput: {
                ...data.getMapRequestsInput,
                searchOption: searchOption,
            },
            searchOptions: data.searchOptions,
            groupedSearchOptions: data.groupedSearchOptions,
            searchFields: data.searchFields,
            zones: data.zones,
            defaultSearchOption: searchOption
        };

        if (!!searchOption) {
            const isSubOrgSelected = isNaN(+searchOption);
            result.getMapRequestsInput.searchOption = isSubOrgSelected ? KlipApi.SearchOption.SubOrganisation : +searchOption as unknown as KlipApi.SearchOption;
            result.getMapRequestsInput.organisationId = isSubOrgSelected ? searchOption : null;
        }

        return result as RESPONSE_TYPE;
    }


    function _mapFromConfirmData(count: number, data: MapRequestsToConfirmData): UnaModel.IMapRequestsToConfirm {
        const searchOption = userLocalStorage.getItem(searchOptionToConfirmLocalStorageKey);
        return _mapFromData<MapRequestsToConfirmData, UnaModel.IMapRequestsToConfirm>(searchOption, count, data);
    }

    function _mapFromToReplyData(count: number, data: MapRequestsToReplyData): UnaModel.IMapRequestsToReply {
        const searchOption = userLocalStorage.getItem(searchOptionToReplyLocalStorageKey);
        return _mapFromData<MapRequestsToReplyData, UnaModel.IMapRequestsToReply>(searchOption, count, data);
    }

    function _mapFromRepliedData(count: number, data: MapRequestsRepliedData): UnaModel.IMapRequestsReplied {
        const searchOption = userLocalStorage.getItem(searchOptionRepliedLocalStorageKey);
        return _mapFromData<MapRequestsRepliedData, UnaModel.IMapRequestsReplied>(searchOption, count, data);
    }

    async function _getMapRequestCounts(response: KlipApi.IMapRequestListViewUna) {
        const mapRequestsToReplyInputForAll = response.mapRequestsToReplyData.getMapRequestsInput;

        const mapRequestsRepliedInputForAll = response.mapRequestsRepliedData.getMapRequestsInput;
        mapRequestsRepliedInputForAll.searchField = SearchField.MyReference;

        const input = KlipApi.GetAllMapRequestCountInput.fromJS({
            mapRequestsToConfirmInput: route.hash === '#toconfirm' ? queryFilter.mapFilterFromQuery(route, response.mapRequestsToConfirmData.getMapRequestsInput, null, queryTypeDefinition) : response.mapRequestsToConfirmData.getMapRequestsInput,
            mapRequestsToReplyInput: route.hash === '#toreply' ? queryFilter.mapFilterFromQuery(route, mapRequestsToReplyInputForAll, { searchOption: 2 }, queryTypeDefinition) : mapRequestsToReplyInputForAll,
            mapRequestsRepliedInput: route.hash === '#replied' ? queryFilter.mapFilterFromQuery(route, mapRequestsRepliedInputForAll, { searchField: 2 }, queryTypeDefinition) : mapRequestsRepliedInputForAll,
        });

        try {
            const mapRequestCountResponse = await useKlipApiProxy().una_GetAllMapRequestCount(input);
            noZonesOrMapRequests.value =
                !!!response.unaZonesCount &&
                mapRequestCountResponse.result.mapRequestRepliedCount === 0 &&
                mapRequestCountResponse.result.mapRequestToConfirmCount === 0 &&
                mapRequestCountResponse.result.mapRequestToReplyCount === 0;

            if (noZonesOrMapRequests.value) {
                return;
            }

            queryFilter.mapColumnsFromQuery(route, response.mapRequestsToConfirmData.mapRequestColumns, KlipApi.MapRequestsToConfirmOrderField);
            toConfirmData.value = _mapFromConfirmData(mapRequestCountResponse.result.mapRequestToConfirmCount, response.mapRequestsToConfirmData);
            queryFilter.mapColumnsFromQuery(route, response.mapRequestsToReplyData.mapRequestColumns, KlipApi.MapRequestsToReplyOrderField);
            toReplyData.value = _mapFromToReplyData(mapRequestCountResponse.result.mapRequestToReplyCount, response.mapRequestsToReplyData)
            queryFilter.mapColumnsFromQuery(route, response.mapRequestsRepliedData.mapRequestColumns, KlipApi.MapRequestsRepliedOrderField);
            repliedData.value = _mapFromRepliedData(mapRequestCountResponse.result.mapRequestRepliedCount, response.mapRequestsRepliedData)
        }
        finally {
            viewModelLoading.value = false;
        }
    }

    return {
        // State
        toConfirm,
        toReply,
        replied,

        toConfirmData,
        toReplyData,
        repliedData,

        noZonesOrMapRequests,
        viewModelLoading,

        // Actions
        initUnaView,
        initToConfirm,
        initReplied,
        initToReply,
        $reset: reset,

        // user actions
        confirmMapRequests,
        notInvolved,
        downloadCurrentReplied,
    };
});
